import qs from 'query-string';
import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

// shared
import { WithEllipsis, WithSkeleton } from '@exchange-frontends/components';
import { useRootHref } from '@exchange-frontends/custom-hooks';
import { getCorrectUrlSlug, sendDataToLaunch } from '@exchange-frontends/utils';

import { ConfigContext } from '../App';

const skeletonImageClass = 'browse_featured_app_card_skeleton_image';
const skeletonImageContainerClass = 'browse_featured_app_card_skeleton_image_container';

const CardContainer = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${({ $isQuiet }) => ($isQuiet ? 'none' : 'var(--spectrum-global-color-gray-50)')};
  border-radius: var(--spectrum-alias-border-radius-regular);
  border: ${({ $isQuiet }) => ($isQuiet ? 'none' : '1px solid var(--spectrum-global-color-gray-200)')};
  cursor: ${({ $showPointer }) => ($showPointer ? 'pointer' : 'default')};
  max-height: 290px;
  width: 100%;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  justify-content: space-between;

  &:hover {
    border: ${({ $isQuiet }) => ($isQuiet ? 'none' : '1px solid var(--spectrum-global-color-gray-400)')};
  }

  & .${skeletonImageClass} {
    line-height: inherit;
    min-height: 150px;
    border-radius: 0;
  }

  & .${skeletonImageContainerClass} {
    width: 100%;
    max-height: 200px;
    height: 100%;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  border-radius: var(--spectrum-alias-border-radius-regular) var(--spectrum-alias-border-radius-regular) 0 0;
`;

const ImageBackgroundStyles = styled.div`
  --size: 200%;
  --offset: calc(calc(var(--size) - 100%) / 2 * -1);
  width: var(--size);
  height: var(--size);
  top: var(--offset);
  left: var(--offset);
  background-position: center;
  background-repeat: no-repeat;
  position: absolute;
  z-index: 0;
  filter: blur(40px);
`;

const ImageBackground = styled(ImageBackgroundStyles)`
  background-image: url(${({ src }) => src});
`;

const Image = styled.img`
  max-height: 200px;
  object-fit: cover;
  width: 100%;
  z-index: 1;
  position: relative;
`;

const ContentContainer = styled.div`
  width: 90%;
  padding: 0 5%;
  border-top: 1px solid var(--spectrum-global-color-gray-200);
`;

const Title = styled.h5`
  margin-bottom: var(--spectrum-global-dimension-size-100);
  margin-top: var(--spectrum-global-dimension-size-150);
  font-size: var(--spectrum-global-dimension-font-size-100);
`;

const Subtitle = styled.p`
  font-size: var(--spectrum-global-dimension-font-size-75);
  margin: 0;
  margin-bottom: var(--spectrum-global-dimension-size-150);
  color: var(--spectrum-global-color-gray-600);
`;

const FeaturedAppCard = ({ title, subtitle, image, isQuiet, showPointer, appId, appType, loading }) => {
  const { ROUTES } = useContext(ConfigContext);
  const appNameSlug = getCorrectUrlSlug(title);
  const link = useRootHref(`${ROUTES.DETAILS}/${appType?.toLowerCase()}/${appId}/${appNameSlug}`);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageErrored, setImageErrored] = useState(false);
  const searchInfo = qs.parse(window.location.search);

  return (
    <CardContainer
      $isQuiet={isQuiet}
      $showPointer={showPointer}
      to={link}
      as={loading ? 'div' : Link}
      onClick={() => sendDataToLaunch('featured', title, searchInfo)}
    >
      <ImageContainer>
        <ImageBackground src={image} />
        <Image
          src={image}
          alt=""
          style={imageLoaded ? {} : { display: 'none' }}
          onLoad={() => {
            setImageLoaded(true);
          }}
          onError={() => {
            setImageErrored(true);
          }}
        />
        {(loading || !imageLoaded) && (
          <WithSkeleton
            shouldRender={false}
            height="100%"
            width="100%"
            enableAnimation={loading && !imageErrored}
            containerClassName={skeletonImageContainerClass}
            className={skeletonImageClass}
          />
        )}
      </ImageContainer>
      <ContentContainer>
        {loading ? (
          <Title>
            <WithSkeleton shouldRender={false} width={200} />
          </Title>
        ) : (
          <WithEllipsis>
            <Title>{title}</Title>
          </WithEllipsis>
        )}
        <Subtitle>
          <WithSkeleton shouldRender={!loading} width={150}>
            {subtitle}
          </WithSkeleton>
        </Subtitle>
      </ContentContainer>
    </CardContainer>
  );
};

export default FeaturedAppCard;
